import Box from "@mui/material/Box";
import CookiesModalContext from "components/CookiesModalContext";
import LanguageContext from "components/LanguageContext";
import PageVersionContext from "components/PageVersionContext";
import SiteContext from "components/SiteContext";
import Icon from "components/templatesComponents/Icon";
import React, { useContext, useMemo, useState } from "react";
import ConsentService from "services/ConsentService";
import { eventKey } from "utils/commonUtils";
import t from "utils/locales/translation.json";

const Share = () => {
  const { language } = useContext(LanguageContext);
  const { currentPageVersion } = useContext(PageVersionContext);
  const { handleOpenCookiesModal } = useContext(CookiesModalContext);
  const site = useContext(SiteContext);

  const [linkCopied, setLinkCopied] = useState(false);

  const cookiesHasRefused = useMemo(() => ConsentService.getConsent() !== "true", []);

  const { fullPath = "", title: pageTitle } = currentPageVersion || {};

  const URL = useMemo(() => `${site.host}${fullPath}`, [fullPath, site.host]);

  const openWindow = (url, title) => {
    const WIDTH = 550;
    const HEIGHT = 285;
    const TOP = (window && window.innerHeight - HEIGHT) / 2;
    const LEFT = (window && window.innerWidth - WIDTH) / 2;
    window?.open(url, title, `height=${HEIGHT},width=${WIDTH},top=${TOP},left=${LEFT},resizable=1`);
  };

  const shares = useMemo(
    () => [
      {
        iconClassName: "fr-share__link--facebook",
        onClick: () => openWindow(`https://www.facebook.com/sharer/sharer.php?u=${URL}`, "Facebook"),
        title: t[language].components.icon_fb_share_title,
        disabled: cookiesHasRefused,
      },
      {
        iconClassName: "fr-share__link--twitter",
        onClick: () => openWindow(`https://twitter.com/intent/tweet?text=${URL}`, "Twitter"),
        title: t[language].components.icon_twitter_share_title,
        disabled: cookiesHasRefused,
      },
      {
        iconClassName: "fr-share__link--linkedin",
        onClick: () => openWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${URL}`, "Linkedin"),
        title: t[language].components.icon_linkedIn_share_title,
        disabled: cookiesHasRefused,
      },
      {
        iconClassName: "fr-share__link--mail",
        onClick: () =>
          openWindow(
            `mailto:?subject=${t[language].components.mail_object} ${site.name}&body=${t[language].components.mail_body1}%0A${pageTitle}%0A%0A${URL}%0A%0A${t[language].components.mail_body2}`,
            "Email",
          ),
        title: t[language].components.icon_mail_share_title,
      },
      {
        iconClassName: "fr-share__link--copy",
        onClick: () => {
          navigator.clipboard.writeText(URL);
          setLinkCopied(true);
        },
        title: t[language].components.copy_link_title,
      },
    ],
    [URL, language, pageTitle, site.name, cookiesHasRefused],
  );

  const handleKeyDown = (event) => {
    if (event.key === eventKey) {
      handleOpenCookiesModal(event);
    }
  };

  return (
    <div className="fr-share">
      <h3 className="fr-share__title fr-text--regular">{t[language].components.share_title}</h3>
      {linkCopied && (
        <Box component="p" className="fr-share__text" sx={{ color: "var(--text-default-success)" }}>
          <Icon iconDSFR="checkbox-circle-fill" aria-hidden="true" sx={{ pr: 0.5 }} />
          {t[language].components.copy_link_message}
        </Box>
      )}
      {cookiesHasRefused && (
        <Box
          component="p"
          className="fr-share__text"
          sx={{ "& > a:not([href])": { color: "inherit", textDecoration: "underline", cursor: "pointer" } }}
        >
          {t[language].components.share_message_1}{" "}
          <a onClick={handleOpenCookiesModal} onKeyDown={handleKeyDown} tabIndex={0}>
            {t[language].components.share_message_2}
          </a>{" "}
          {t[language].components.share_message_3}
        </Box>
      )}
      <ul className="fr-share__group">
        {shares.map((share) => {
          const { title, iconClassName, onClick, disabled } = share;
          return (
            <li key={title}>
              <button
                type="button"
                className={`fr-share__link ${iconClassName}`}
                title={title}
                onClick={onClick}
                disabled={disabled}
              >
                {title}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Share;
